import type { FieldSchema } from '@tsagko/label-contracts'

export type SampleMode = 'sample' | 'empty' | 'torture'

export const SAMPLE_VALUES: Record<string, unknown> = {
  productName: 'Beef mince premium',
  productCode: 'BMP-500',
  brand: 'DEVESA',
  category: 'meat',
  gtin: '5201234567890',
  weight: 0.523,
  unit: 'kg',
  price: 4.75,
  manufacturedAt: '2026-04-01',
  expiresAt: '2026-05-15',
  bestBefore: '2026-05-15',
  countryOfOrigin: 'Greece',
  allergens: 'contains: wheat, soy',
  producerLogo: undefined,
}

const MAX_STRING =
  'Long Long Long Long Long Long Long Long Long Long Long Long Long product name 1234567890'

export const TORTURE_VALUES: Record<string, unknown> = {
  productName: MAX_STRING,
  productCode: 'XXXXXXXXXXXXXXXXXXXXXXXXXXXX',
  brand: 'Verylongbrandnamefortesting',
  category: 'meat',
  gtin: '9999999999993',
  weight: 99.999,
  unit: 'kg',
  price: 999999.99,
  manufacturedAt: '2026-12-31',
  expiresAt: '2099-12-31',
  bestBefore: '2099-12-31',
  countryOfOrigin: 'Republic of the Seychelles and adjoining islands',
  allergens:
    'contains: wheat, soy, eggs, milk, peanuts, tree nuts, fish, shellfish, sesame, mustard',
}

export const EMPTY_VALUES: Record<string, unknown> = {
  productName: '',
  productCode: '',
  brand: '',
  category: '',
  gtin: '',
  weight: 0,
  unit: '',
  price: 0,
  manufacturedAt: '',
  expiresAt: '',
  bestBefore: '',
  countryOfOrigin: '',
  allergens: '',
}

/**
 * Legacy hardcoded sample-value lookup. Still used by `PrintDialog.tsx`,
 * which always prints against the fixed 'sample' tier regardless of the
 * mounted schema. New schema-aware call sites should use
 * `generateSampleValues` instead.
 */
export function getSampleData(mode: SampleMode): Record<string, unknown> {
  switch (mode) {
    case 'sample':
      return SAMPLE_VALUES
    case 'torture':
      return TORTURE_VALUES
    case 'empty':
      return EMPTY_VALUES
  }
}

// ---------------------------------------------------------------------------
// Schema-driven generator
// ---------------------------------------------------------------------------

function synthText(label: string, mode: SampleMode): string {
  if (mode === 'empty') return ''
  if (mode === 'torture') {
    return `Long Long Long Long Long Long Long Long Long Long Long Long Long ${label} 1234567890`
  }
  return `${label} sample`
}

function synthNumber(precision: number, mode: SampleMode): number {
  // Clamp before toFixed, which throws RangeError outside [0, 100] — the
  // precision comes from a host schema that crosses the wire un-narrowed,
  // so a misbehaving host must degrade to a lower precision, not crash the
  // preview.
  const digits = Math.min(Math.max(precision, 0), 100)
  if (mode === 'empty') return 0
  if (mode === 'torture') return Number((999999.99).toFixed(digits))
  return Number((12.34).toFixed(digits))
}

function synthDate(mode: SampleMode): string {
  if (mode === 'empty') return ''
  if (mode === 'torture') return '2099-12-31'
  return new Date().toISOString().slice(0, 10)
}

function synthEnumValue(values: string[], mode: SampleMode): string {
  if (mode === 'empty') return ''
  return values[0] ?? ''
}

function synthImagePlaceholder(mode: SampleMode): string | undefined {
  if (mode === 'empty') return undefined
  return 'placeholder-image'
}

function synthBoolean(mode: SampleMode): boolean {
  return mode !== 'empty'
}

/**
 * Type-driven synthetic value for a single field, per the mode's tier
 * (sample/empty/torture). Handles both primitive `FieldType` strings and
 * the structured kinds (measurement/range/currency/localized-enum) — the
 * structured numeric kinds all route through `synthNumber` with their own
 * precision; `localized-enum` routes through the same first-value rule as
 * primitive `enum`.
 */
function synthFieldValue(field: FieldSchema, mode: SampleMode): unknown {
  const { type } = field
  if (typeof type === 'string') {
    switch (type) {
      case 'text':
        return synthText(field.label, mode)
      case 'number':
        return synthNumber(2, mode)
      case 'date':
        return synthDate(mode)
      case 'boolean':
        return synthBoolean(mode)
      case 'image':
        return synthImagePlaceholder(mode)
      case 'enum':
        return synthEnumValue(field.enumValues ?? [], mode)
    }
  }
  switch (type.kind) {
    case 'measurement':
      return synthNumber(type.precision, mode)
    case 'currency':
      return synthNumber(2, mode)
    case 'range':
      return synthNumber(2, mode)
    case 'localized-enum':
      return mode === 'empty' ? '' : (type.values[0]?.key ?? '')
    default: {
      // Compile-time exhaustive over today's FieldType union — adding a new
      // structured kind to the contracts fails this `satisfies never` until
      // it gets a synth rule above. At runtime we can still land here (host
      // schemas cross the wire un-narrowed), so the fallback is loud rather
      // than silently blank.
      void (type satisfies never)
      const kind = (type as { kind?: unknown }).kind
      console.warn(
        `generateSampleValues: unhandled field type kind "${String(kind)}" for field "${field.key}" — no sample value synthesized`
      )
      return undefined
    }
  }
}

/**
 * Schema-driven sample value generator. Replaces the hardcoded
 * `SAMPLE_VALUES` lookup for any schema — demo or a real mounted schema
 * from `SessionContext.schemaMeta.fields` — by synthesizing a
 * type-appropriate value per field for the given mode, keyed by
 * `field.key`.
 */
export function generateSampleValues(
  fields: FieldSchema[],
  mode: SampleMode
): Record<string, unknown> {
  const result: Record<string, unknown> = {}
  for (const field of fields) {
    result[field.key] = synthFieldValue(field, mode)
  }
  return result
}
